/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { Children, useEffect, useState, useRef, forwardRef } from 'react';

import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';
import { motion } from 'framer-motion';

import { Tooltip } from 'uikit/tooltips';
import { useOnClickOutside } from 'uikit/utils';

import MoreHoriz from '@mui/icons-material/MoreHoriz';

import MenuList from './menuList';
import Button from '../../buttons/components/button';

import styles from '../styles/uikitMenusMenu.module.scss';

const Menu = forwardRef(
  (
    {
      children,
      className = '',
      component = (
        <Button
          icon={<MoreHoriz />}
          variant="ghost"
          size="small"
          ariaLabel={i18n.t('more', 'More')}
        />
      ),
      contentClassName = '',
      horizontalPosition = 'right',
      isBreadcrumbs = false,
      menuListClassName = '',
      onIsOpenChange = () => {},
      overlay = null,
      shouldAnimate = false,
      shouldCloseOnMouseLeave = false,
      shouldCloseOnTab = true,
      shouldOpenOnFocus = false,
      shouldSetFocus = true,
      testId = '',
      tooltip = '',
      variant = '',
      verticalPosition = 'bottom',
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuListHeight, setMenuListHeight] = useState(0);

    const buttonRef = useRef();
    const menuRef = useRef();
    const menuContentRef = useRef();
    const menuListRef = useRef();

    useEffect(() => onIsOpenChange({ buttonRef, isOpen, menuListRef, menuRef }), [isOpen]);

    useEffect(() => {
      if (isOpen && menuListRef.current) {
        setMenuListHeight(menuListRef.current.offsetHeight);
      } else {
        setMenuListHeight(0);
      }
    }, [isOpen]);

    useOnClickOutside({ ref: menuRef, callback: () => setIsOpen(false) });

    if (!children) return null;

    const handleBlur = ({ currentTarget, relatedTarget }) => {
      if (
        (relatedTarget &&
          !currentTarget.contains(relatedTarget) &&
          !menuListRef.current?.contains(relatedTarget)) ||
        (!shouldSetFocus && relatedTarget === null)
      ) {
        setIsOpen(false);
      }
    };

    const handleOnClick = (event) => {
      const menuListItems = menuListRef.current.childNodes;

      menuListItems.forEach((item) => {
        if (item.contains(event.target) && item.hasAttribute('role')) {
          setIsOpen(false);
        }
      });
    };

    const handleOnListKeyDown = (event) => {
      if (event.key === 'Tab' && shouldCloseOnTab) {
        setIsOpen(false);
      }

      if (event.key === 'Enter' || event.key === 'Escape') {
        setIsOpen(false);
        return ref || buttonRef.current.focus();
      }
    };

    const variants = {
      open: { y: 3, opacity: 1, display: 'block' },
      closed: { y: 0, opacity: 0, transitionEnd: { display: 'none' } },
    };

    const getContentStyles = () => {
      if (!buttonRef.current) {
        return {};
      }

      if (verticalPosition === 'auto' && !menuListHeight) {
        return { visibility: 'hidden' };
      }

      const buttonPosition = buttonRef.current.getBoundingClientRect();
      const verticalPositionMapping = { top: 'bottom', bottom: 'top' };
      const isHorizontallyCentered = horizontalPosition === 'center';

      let finalVerticalPosition = verticalPosition;

      const positionStyles = {
        [verticalPositionMapping[finalVerticalPosition]]: buttonPosition.height,
        ...(!isHorizontallyCentered
          ? { [horizontalPosition]: 0 }
          : {
              left: '-50%',
              transform: 'translateX(-50%)',
              marginLeft: `${buttonPosition.width}px`,
            }),
      };

      if (verticalPosition !== 'auto') {
        return positionStyles;
      }

      const menuContentMaxHeight = parseInt(
        window.getComputedStyle(menuContentRef?.current).getPropertyValue('height'),
        10,
      );

      const maxHeight = Number.isNaN(menuContentMaxHeight) ? menuListHeight : menuContentMaxHeight;

      const spaceAboveButton = buttonPosition.top;
      const spaceBelowButton = window.innerHeight - buttonPosition.bottom;

      if (spaceBelowButton < maxHeight) {
        finalVerticalPosition = 'top';
      } else if (spaceAboveButton < maxHeight) {
        finalVerticalPosition = 'bottom';
      }

      return {
        ...positionStyles,
        [verticalPositionMapping[finalVerticalPosition]]: buttonPosition.height,
        visibility: 'visible',
      };
    };

    const optionAttributes = () => {
      const attributes = {};

      if (shouldCloseOnMouseLeave && isOpen) {
        attributes.onMouseLeave = () => setIsOpen(false);
      }

      return attributes;
    };

    const TriggerElement = React.cloneElement(component, {
      'aria-expanded': isOpen ? 'true' : 'false',
      'aria-haspopup': true,
      'data-testid': 'menu-button',
      onClick: (event) => {
        // eslint-disable-next-line react/prop-types
        component.props.onClick?.(event);
        setIsOpen(!isOpen);
      },
      ...(shouldOpenOnFocus && {
        onFocus: () => setIsOpen(true),
        onBlur: handleBlur,
      }),
      onMouseDown: (event) => event.preventDefault(),
      ref: ref || buttonRef,
    });

    const Element = shouldAnimate ? motion.div : 'div';
    const ElementProps = shouldAnimate
      ? {
          initial: 'closed',
          animate: isOpen ? 'open' : 'closed',
          transition: { duration: 0.3 },
          variants,
        }
      : undefined;

    const items = Children.toArray(children).map((child) => {
      if (!React.isValidElement(child)) {
        return;
      }
      return child;
    });

    const CLASSES = {
      variant: {
        local: {
          bounded: styles.isVariantBounded,
          unbounded: styles.isVariantUnbounded,
          userMenu: styles.isVariantUserMenu,
        },
        global: {
          bounded: 'menuIsVariantBounded',
          unbounded: 'menuIsVariantUnbounded',
          userMenu: 'menuIsVariantUserMenu',
        },
      },
    };

    return (
      <div
        className={classnames(
          styles.element,
          { [CLASSES.variant.global[variant]]: variant },
          className,
        )}
        {...optionAttributes()}
        ref={menuRef}
        data-testid={testId}
      >
        {tooltip ? <Tooltip content={tooltip}>{TriggerElement}</Tooltip> : TriggerElement}
        {isOpen && (
          <Element
            {...ElementProps}
            className={classnames(styles.content, contentClassName, {
              [CLASSES.variant.local[variant]]: variant,
              [styles.isOpen]: isOpen,
              [styles.isNotAnimated]: !shouldAnimate,
            })}
            ref={menuContentRef}
            data-testid="menu-div"
            onClick={handleOnClick}
            style={getContentStyles()}
          >
            {overlay}
            <MenuList
              className={menuListClassName}
              isBreadcrumbs={isBreadcrumbs}
              onBlur={handleBlur}
              onKeyDown={handleOnListKeyDown}
              ref={menuListRef}
              shouldSetFocus={shouldSetFocus}
            >
              {items}
            </MenuList>
          </Element>
        )}
      </div>
    );
  },
);

Menu.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  component: PropTypes.node,
  contentClassName: PropTypes.string,
  horizontalPosition: PropTypes.oneOf(['left', 'right', 'center']),
  isBreadcrumbs: PropTypes.bool,
  menuListClassName: PropTypes.string,
  onIsOpenChange: PropTypes.func,
  overlay: PropTypes.node,
  shouldAnimate: PropTypes.bool,
  shouldCloseOnMouseLeave: PropTypes.bool,
  shouldCloseOnTab: PropTypes.bool,
  shouldOpenOnFocus: PropTypes.bool,
  shouldSetFocus: PropTypes.bool,
  testId: PropTypes.string,
  tooltip: PropTypes.string,
  variant: PropTypes.oneOf(['bounded', 'unbounded', 'userMenu']),
  verticalPosition: PropTypes.oneOf(['top', 'bottom', 'auto']),
};

export { Menu as MenuStorybook };
export default React.memo(Menu);
